import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Alert,
    Button,
    Divider,
    Group,
    Modal,
    Paper,
    Radio,
    Select,
    Stack,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import {
    assignVisit,
    createVisit,
    linkVisitPoi,
    listUsers,
    transitionVisitStatus,
    updateVisitData,
} from '../../api/resources';
import { userLabel } from '../../lib/labels';
import { VisitStatus } from '../../types/domain';

/**
 * Rejecting is rarely the end of the work — the visit usually has to be redone.
 * This runs the whole outcome as one action: reject, explain why, and optionally
 * raise a follow-up visit assigned to the same or a different officer.
 *
 * The note the officer reads is the follow-up's `instructions`; the rejection
 * text lands on the rejected visit instead. They are deliberately separate
 * fields — the officer opens the new visit, not the rejected one.
 */
export function RejectVisitModal({
    opened,
    onClose,
    visitId,
    caseId,
    visitType,
    currentOfficerId,
    poiId,
    onDone,
}: {
    opened: boolean;
    onClose: () => void;
    visitId: string;
    caseId?: string;
    visitType?: string;
    currentOfficerId?: string;
    /** Address the rejected visit targeted; carried onto the follow-up. */
    poiId?: string;
    onDone?: () => void;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [reason, setReason] = useState('');
    const [comments, setComments] = useState('');
    const [createFollowUp, setCreateFollowUp] = useState(true);
    /** Defaults to the officer who did the work — "same" should be the easy path. */
    const [officerId, setOfficerId] = useState<string | null>(currentOfficerId ?? null);
    const [note, setNote] = useState('');

    const usersQ = useQuery({
        queryKey: ['users-field-officers'],
        queryFn: () => listUsers({ page: 1, limit: 500 }),
    });
    const fieldOfficers = (usersQ.data?.data ?? []).filter((u) => !u.isPlatformAdmin);

    const rejectMutation = useMutation({
        mutationFn: async () => {
            await transitionVisitStatus(visitId, { toStatus: VisitStatus.Rejected });

            const trimmedReason = reason.trim();
            const trimmedComments = comments.trim();
            if (trimmedReason || trimmedComments) {
                await updateVisitData(visitId, {
                    ...(trimmedReason ? { rejectionReason: trimmedReason } : {}),
                    ...(trimmedComments ? { rejectionComments: trimmedComments } : {}),
                });
            }

            if (!createFollowUp || !caseId) {
                return { followUpCreated: false };
            }

            const trimmedNote = note.trim();
            const followUp = await createVisit(caseId, {
                ...(visitType ? { visitType } : {}),
                ...(trimmedNote ? { instructions: trimmedNote } : {}),
                /**
                 * The backend only auto-links an address when the case has exactly
                 * one POI. Re-send this visit's own address so a multi-address case
                 * doesn't produce a follow-up with nowhere to go.
                 */
                ...(poiId ? { skipAutoVisitPoi: true } : {}),
            });

            if (poiId) {
                await linkVisitPoi(followUp.id, { poiId });
            }

            /**
             * Assign separately rather than passing `fieldOfficerId` to create:
             * only the assign endpoint records the assignment for the audit trail.
             */
            if (officerId) {
                await assignVisit(followUp.id, {
                    assignedAgentId: officerId,
                    ...(trimmedReason
                        ? { reason: t('features.visits.followUpOf', { reason: trimmedReason }) }
                        : {}),
                });
            }

            return { followUpCreated: true };
        },
        onSuccess: ({ followUpCreated }) => {
            notifications.show({
                color: 'green',
                message: followUpCreated
                    ? t('features.visits.rejectedWithFollowUp')
                    : t('features.visits.rejected'),
            });
            onClose();
            void queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
            void queryClient.invalidateQueries({ queryKey: ['visits'] });
            void queryClient.invalidateQueries({ queryKey: ['assignment-queue'] });
            onDone?.();
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    /** Without a case there is nothing to hang a follow-up visit on. */
    const canFollowUp = Boolean(caseId);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('features.visits.rejectTitle')}
            size="lg"
        >
            <Stack>
                <TextInput
                    label={t('features.visits.rejectionReason')}
                    placeholder={t('features.visits.rejectionReasonPlaceholder')}
                    value={reason}
                    onChange={(e) => setReason(e.currentTarget.value)}
                />
                <Textarea
                    label={t('features.visits.rejectionComments')}
                    minRows={2}
                    value={comments}
                    onChange={(e) => setComments(e.currentTarget.value)}
                />

                <Divider />

                {/* Force an explicit choice so a follow-up isn't skipped by accident. */}
                <Text fw={600} size="sm">
                    {t('features.visits.followUpDecision')}
                </Text>

                {!canFollowUp ? (
                    <Alert color="yellow" variant="light">
                        {t('features.visits.followUpNeedsCase')}
                    </Alert>
                ) : (
                    <Radio.Group
                        value={createFollowUp ? 'followup' : 'none'}
                        onChange={(v) => setCreateFollowUp(v === 'followup')}
                    >
                        <Stack gap="sm">
                            <Paper
                                withBorder
                                p="md"
                                radius="md"
                                onClick={() => setCreateFollowUp(true)}
                                style={{
                                    cursor: 'pointer',
                                    borderColor: createFollowUp
                                        ? 'var(--mantine-color-blue-filled)'
                                        : undefined,
                                    borderWidth: 2,
                                }}
                            >
                                <Radio
                                    value="followup"
                                    label={t('features.visits.createFollowUp')}
                                    description={t('features.visits.createFollowUpHint')}
                                />
                            </Paper>
                            <Paper
                                withBorder
                                p="md"
                                radius="md"
                                onClick={() => setCreateFollowUp(false)}
                                style={{
                                    cursor: 'pointer',
                                    borderColor: !createFollowUp
                                        ? 'var(--mantine-color-red-filled)'
                                        : undefined,
                                    borderWidth: 2,
                                }}
                            >
                                <Radio
                                    value="none"
                                    color="red"
                                    label={t('features.visits.rejectOnly')}
                                    description={t('features.visits.rejectOnlyHint')}
                                />
                            </Paper>
                        </Stack>
                    </Radio.Group>
                )}

                {canFollowUp && !createFollowUp ? (
                    <Alert
                        color="red"
                        variant="light"
                        icon={<IconAlertTriangle size={18} />}
                    >
                        {t('features.visits.rejectOnlyWarning')}
                    </Alert>
                ) : null}

                {createFollowUp && canFollowUp ? (
                    <Stack gap="sm">
                        <Select
                            label={t('fields.fieldOfficer')}
                            placeholder={t('features.visits.followUpOfficerPlaceholder')}
                            clearable
                            searchable
                            data={fieldOfficers.map((u) => ({ value: u.id, label: userLabel(u) }))}
                            value={officerId}
                            onChange={setOfficerId}
                        />
                        <Textarea
                            label={t('features.visits.noteToOfficer')}
                            description={t('features.visits.noteToOfficerHint')}
                            minRows={3}
                            value={note}
                            onChange={(e) => setNote(e.currentTarget.value)}
                        />
                        {!officerId ? (
                            <Text size="xs" c="dimmed">
                                {t('features.visits.followUpUnassignedHint')}
                            </Text>
                        ) : null}
                    </Stack>
                ) : null}

                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose}>
                        {t('common.actions.cancel')}
                    </Button>
                    <Button
                        color="red"
                        loading={rejectMutation.isPending}
                        onClick={() => rejectMutation.mutate()}
                    >
                        {t('features.visits.rejectAction')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
